/* eslint-disable import/no-cycle */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { FormGroup } from '@angular/forms'

import { Observable } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxFormUtilitiesModule } from '@flowx/angular-ui-toolkit'
import { FormFieldTemplateConfig, FormTemplateConfig } from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxFormsService } from '../../services/flx-forms.service'
import { FlxFormFieldComponent } from '../flx-form-field/flx-form-field.component'
import { getBoolExpressionSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-form',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxFormUtilitiesModule,
    FlxLocalizePipe,
    FlxFormFieldComponent,
    FlxThemeModule,
    FlxStyleModule,
  ],
  templateUrl: './flx-form.component.html',
  styleUrl: './flx-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FlxFormComponent implements OnInit, OnDestroy, AfterViewInit {
  #formService = inject(FlxFormsService)
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<FormTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  disabledState = computed(() => {
    const isDisabled = this.isDisabled()
    const isHidden = this.isHidden()
    return isDisabled || isHidden
  })

  templateConfigWithCollectionMeta = computed<FormFieldTemplateConfig[]>(() => {
    const { templateConfig, flowxUuid, prototypeInstanceMeta } = this.templateConfig()
    return templateConfig.map((config) => ({
      ...config,
      originalFlowxUuid: config.originalFlowxUuid || config.flowxUuid,
      flowxUuid: prototypeInstanceMeta ? `${config.flowxUuid}-${flowxUuid}` : config.flowxUuid,
      prototypeInstanceMeta,
    }))
  })

  formGroup: FormGroup
  formSubmitted$: Observable<boolean>

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isHidden: Signal<boolean>
  isDisabled: Signal<boolean>
  // dynamic props
  label: Signal<string>

  readonly labelId = crypto.randomUUID()

  ngOnInit(): void {
    const formId = this.templateConfig().flowxUuid
    this.formGroup = this.#formService.buildForm(
      this.templateConfig(),
      this.templateConfigWithCollectionMeta()
    )
    this.formSubmitted$ = this.#formService.formSubmitted$(
      formId,
      this.templateConfig().reusableUiTemplateContext
    )

    runInInjectionContext(this.#injector, () => {
      const { hide, disabled, label } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.isDisabled = getBoolExpressionSignal(disabled, this.templateConfig(), {
        logType: 'disabled',
      })
      this.label = getDynamicPropSignal(label, this.templateConfig())

      // disable form if disabled or hidden so that the form does not participate in action validation
      effect(() => {
        const isDisabled = this.isDisabled()
        const isHidden = this.isHidden()
        if (isDisabled || isHidden) {
          this.formGroup.disable({ emitEvent: false })
        } else {
          this.formGroup.enable({ emitEvent: false })
        }
      })

      const { conditionals } = this.style()
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  preventSubmitOnEnter(event: KeyboardEvent): void {
    if ((event.target as HTMLElement).tagName === 'INPUT') {
      event.preventDefault()
    }
  }

  ngOnDestroy(): void {
    this.#formService.removeFromValidation(this.templateConfig().flowxUuid)
  }
}
